import { ClearOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, MailOutlined } from "@ant-design/icons";
import { Badge, Button, Col, Form, Input, Modal, Popconfirm, Row, Space, Table, Typography, notification } from "antd";
import { useCreateRemindEmailsMutation, useCreateSendEmailsMutation, useDeleteUserByIdMutation, useDeleteUsersMutation, useEditEmailUserMutation, useGetUsersQuery, useLazySendEmailQuery } from "api";
import { OBJ_COLOR, USER_ADMIN } from "commons/constants";
import { debounce } from "lodash";
import { User as UserModal } from "models";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "store";
import styled from "styled-components";
import { stringToSlug } from "utils";
import { ExportDataNoEmail } from "./ExportDataNoEmail";
import { ExportDataUsers } from "./ExportDataUsers";
import { ExportStatusUsers } from "./ExportStatusUsers";
import { HighLightCard } from "./HighLightCard";
import { TableUsersExpandable } from "./TableUsersExpandable";
import { Template } from "./Template";

const { confirm } = Modal;

const { Title, Text } = Typography;

const CustomsSpace = styled(Space)`
	display: flex;
	justify-content: space-between;
	@media (max-width: 750px) {
		display: none;
	}
`;

export const User = () => {
	const user = useAppSelector((state) => state.user);

	const { data: users, isLoading } = useGetUsersQuery(undefined);
	const [createSendEmails] = useCreateSendEmailsMutation();
	const [createRemindEmails] = useCreateRemindEmailsMutation();
	const [deleteUsers] = useDeleteUsersMutation();
	const [deleteUserById] = useDeleteUserByIdMutation();
	const [editEmailUser] = useEditEmailUserMutation();
	const [sendEmail] = useLazySendEmailQuery();

	const [searchValue, setSearchValue] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [dataSource, setDataSource] = useState<UserModal[]>([]);

	const debounceSearch = useRef(
		debounce((searchValue: string, users: UserModal[]) => {
			if (!users || !users.length) return setDataSource([]);
			const data = users.filter(
				(user) => user.id.indexOf(searchValue) !== -1 || stringToSlug(user.name).indexOf(stringToSlug(searchValue)) !== -1 || (user.email && user.email.indexOf(searchValue) !== -1)
			);
			setDataSource(data);
		}, 200)
	);

	const [form] = Form.useForm();

	useEffect(() => {
		debounceSearch.current(searchValue, users || []);
	}, [searchValue, users]);

	const handleOnClickDeleteUsers = async () => {
		try {
			await deleteUsers(undefined).unwrap();
			notification.success({
				message: "Success",
			});
		} catch (error: any) {
			notification.error({
				message: error.data.message,
			});
		}
	};

	const handleOnClickSendEmail = async () => {
		try {
			await createSendEmails({ userId: user?.id }).unwrap();
			notification.success({
				message: "Success",
			});
		} catch (error: any) {
			notification.error({
				message: error.data.message,
				description: "Hệ thống đang gửi email !",
			});
		}
	};

	const handleOnClickRemindEmail = async () => {
		try {
			await createRemindEmails({ userId: user?.id }).unwrap();
			notification.success({
				message: "Success",
			});
		} catch (error: any) {
			notification.error({
				message: error.data.message,
				description: "Hệ thống đang gửi email !",
			});
		}
	};

	const handleDeleteUser = async (id: string) => {
		confirm({
			title: "Bạn có chắc chắn muốn xóa ?",
			icon: <ExclamationCircleOutlined />,
			content: "Nếu xóa đồng nghĩa câu trả lời cũng bị xóa theo.",
			async onOk() {
				try {
					await deleteUserById(id).unwrap();
					notification.success({
						message: "Success",
					});
				} catch (error: any) {
					notification.error({
						message: error.data.message,
					});
				}
			},
		});
	};

	const handleEditUser = async (user: UserModal) => {
		form.setFieldValue("id", user.id);
		form.setFieldValue("name", user.name);
		form.setFieldValue("email", user.email);
		setIsModalOpen(true);
	};

	const handleOk = () => {
		form.submit();
	};

	const handleCancel = () => {
		setIsModalOpen(false);
	};

	const handleOnFinish = async (values: { id: string; name: string; email: string }) => {
		try {
			const { id, email } = values;
			await editEmailUser({ id, email }).unwrap();
			notification.success({
				message: "Success",
			});
		} catch (error: any) {
			notification.error({
				message: error.data.message,
			});
		}
		setIsModalOpen(false);
	};

	return (
		<>
			<Row gutter={[0, 20]}>
				<Col span={24}>
					<Space direction="vertical" size={"large"} style={{ width: "100%" }}>
						<Title style={{ color: OBJ_COLOR.SUCCESS }} level={3}>
							Thống kê nhân viên
						</Title>
						<Space style={{ display: "flex", justifyContent: "space-between" }}>
							{/* <Space>
								<Popconfirm disabled={!users?.length ? true : false} placement="bottomRight" title="Sure to send email?" onConfirm={handleOnClickSendEmail}>
									<Button type="primary" icon={<SendOutlined />}>
										Send Email
									</Button>
								</Popconfirm>

								<Popconfirm disabled={!users?.length ? true : false} placement="bottomRight" title="Sure to remind email?" onConfirm={handleOnClickRemindEmail}>
									<Button type="primary" ghost icon={<BellOutlined />}>
										Remind email
									</Button>
								</Popconfirm>
							</Space> */}
							<CustomsSpace>
								<ExportDataNoEmail />
							</CustomsSpace>
						</Space>
						<HighLightCard />
					</Space>
				</Col>
				<Col span={24}></Col>
				<Col span={24}>
					<Title style={{ color: OBJ_COLOR.SUCCESS, margin: 0 }} level={3}>
						Danh sách nhân viên
					</Title>
				</Col>
				<Col span={24}>
					<CustomsSpace>
						<Space style={{ display: "flex", justifyContent: "space-between" }}>
							<Template nameSheet={"Danh sách Nhân viên"} header={["stt", "userId", "name", "email", "language"]} />
							{USER_ADMIN.includes(user?.id as string) && (
								<Popconfirm disabled={!users?.length ? true : false} placement="topLeft" title="Sure to cancel?" onConfirm={handleOnClickDeleteUsers}>
									<Button icon={<ClearOutlined />} danger disabled={!users?.length ? true : false}>
										Clear Data
									</Button>
								</Popconfirm>
							)}
						</Space>
						<Space>
							{USER_ADMIN.includes(user?.id as string) && <ExportDataUsers />}
							<ExportStatusUsers users={users} />
						</Space>
					</CustomsSpace>
				</Col>
				<Col span={24}>
					<Space size={"large"} direction="vertical" style={{ width: "100%", margin: "20px 0" }}>
						<Space style={{ float: "right" }}>
							<Text type="secondary">Tìm kiếm:</Text>
							<Input onChange={(value) => setSearchValue(value.target.value)} value={searchValue} placeholder="Nhập nội dung..." />
						</Space>
						<Table
							pagination={{
								showSizeChanger: true,
								hideOnSinglePage: true,
							}}
							dataSource={dataSource.map((item, index) => ({ ...item, key: index + 1 }))}
							loading={isLoading}
							scroll={{ x: true }}
							style={{ whiteSpace: "nowrap" }}
							expandable={{
								expandedRowRender: (record: UserModal) => {
									return <TableUsersExpandable userId={record.id} />;
								},
								rowExpandable: () => USER_ADMIN.includes(user?.id as string),
							}}>
							<Table.Column title="STT" dataIndex="key" />
							<Table.Column<UserModal>
								title="Id user"
								render={(_, record) => {
									return record?.id;
								}}
							/>
							<Table.Column<UserModal>
								title="Name"
								render={(_, record) => {
									return record?.name;
								}}
							/>
							<Table.Column<UserModal>
								title="Email"
								render={(_, record) => {
									return record?.email;
								}}
							/>
							<Table.Column<UserModal>
								title="Language"
								render={(_, record) => {
									return record?.language.name;
								}}
							/>
							<Table.Column<UserModal>
								title="Trạng thái user"
								align="center"
								render={(_, record) => {
									return record?.status === 2 ? (
										<Badge style={{ background: OBJ_COLOR.SUCCESS }} count={"Đã hoàn thành"} />
									) : record?.status === 1 ? (
										<Badge style={{ background: OBJ_COLOR.INFO }} count={"Đã xem"} />
									) : (
										<Badge style={{ background: OBJ_COLOR.DANGER }} count={"Chưa xem"} />
									);
								}}
								sorter={(a, b) => a.status - b.status}
							/>
							<Table.Column<UserModal>
								title=""
								align="center"
								render={(_, record) => {
									return (
										<Space size={20}>
											<EditOutlined onClick={() => handleEditUser(record)} style={{ fontSize: 16, color: OBJ_COLOR.INFO }} />
											<DeleteOutlined onClick={() => handleDeleteUser(record.id)} style={{ fontSize: 16, color: OBJ_COLOR.DANGER }} />

											<Popconfirm
												placement="left"
												title="Bạn có muốn gửi mail không ?"
												onConfirm={async () => {
													try {
														await sendEmail(record.email).unwrap();
													} catch (error: any) { }

													notification.success({
														message: "Success",
													});
												}}
												okText="Có"
												cancelText="Không">
												<MailOutlined style={{ fontSize: 16, color: OBJ_COLOR.SUCCESS }} />
											</Popconfirm>
										</Space>
									);
								}}
							/>
						</Table>
					</Space>
				</Col>
			</Row>
			<Modal title="Thay đổi email" visible={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
				<Form form={form} layout="vertical" onFinish={handleOnFinish}>
					<Form.Item name={"id"} label="Mã nhân viên">
						<Input placeholder="Vui lòng nhập mã nhân viên" readOnly />
					</Form.Item>
					<Form.Item name={"name"} label="Họ Tên">
						<Input placeholder="Vui lòng nhập họ tên" readOnly />
					</Form.Item>
					<Form.Item name={"email"} label="Email" rules={[{ required: true, message: "Vui lòng nhập email" }]}>
						<Input placeholder="Vui lòng nhập email" />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};
